import {
  CutlistMaterialGroup,
  Material,
  MaterialGroup,
  MaterialGroupState,
  PartItem,
  SHEET_STACKING_OPTIONS,
} from '@cutr/constants/cutlist';
import { BackendCutlistGroup } from '@cutr/constants/cutlist-backend';
import type { Features } from '@cutr/constants/cutlist-theme.ts';
import * as Sentry from '@sentry/react';
import { nanoid } from 'nanoid';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

import { materialGroupMapper, partMapper } from '@/hooks';
import {
  useCreateMaterialGroup,
  useUpdateMaterialGroup,
} from '@/queries/materialGroup';
import { isStarterTier } from '@/theme';
import {
  createBackendGrouping,
  createBackendGroupingForNesting,
} from '@/utils/backend-compatibility';
import { findMatchingMaterialsWithFallback } from '@/utils/edgeband';
import {
  applyCustomTrimThickness,
  getTrimConfigByType,
} from '@/utils/features';
import { useDebounce } from '@/utils/hooks';
import { hashObject, omit, pick } from '@/utils/misc';
import {
  getSmallestFittingMaterial,
  groupPartsByMaterial,
} from '@/utils/nesting';

import { api } from './backend';
import { useIsLoggedIn } from './login';
import {
  getEdgebandingMaterials,
  getHPLMaterials,
  getMaterial,
} from './materials';
import { useNestingStatusStore, useNestingStore } from './nesting';
import { selectHasMaterials, useCutlistParts, useCutlistState } from './store';

export type MaterialComponent = 'core1' | 'topHpl' | 'bottomHpl' | 'edgeband';

type MaterialGroupStateActions = {
  addGroup(group?: Partial<MaterialGroup>): MaterialGroup['id'];
  addGroupsByArticleCodes(
    articleCodes: Material['articleCode'][],
    currentFeatures: Features,
    activeGroupArticleCode?: string
  ): Promise<void>;
  addBeamGroupByArticleCode(beamMaterial: Material): void;
  setGroup(group: MaterialGroup): void;
  removeGroup(id: string): void;
  removeEmptyGroups(): void;
  setActive(id: string): void;
  clear(): void;
  groupBy(): void;
  init(
    state: MaterialGroupState,
    options?: { missingMaterials?: string[] }
  ): void;

  // These functions are used by the Agent Quoting flow
  addBackendGroup(group: MaterialGroup): void;
  setGroupIndex(groupId: string, index: number): void;
};

export const generateMaterialGroup = (
  materialGroup?: Partial<MaterialGroup>
): MaterialGroup => ({
  id: nanoid(6),
  name: '',
  core1: null,
  core2: null,
  topHpl: null,
  bottomHpl: null,
  materialSandwichType: null,
  sheetEdgeTrimConfig: null,
  sheetSizeSelection: 'manual',
  automaticSheetSizeMaterials: [],
  edgeProfile: 'none',
  edgeband: null,
  createLabels: false,
  aiSuggestions: {},
  beamMaterialId: null,
  sheetStackingType: SHEET_STACKING_OPTIONS.disabled,
  ...materialGroup,
});

export const setGroupAndParts = (group: MaterialGroup) => {
  useMaterialGroupState.getState().setGroup(group);
  useCutlistState.getState().updatePartsFromGroup(group);
};

export const updateAutomaticSheetSizeMaterials = (group: MaterialGroup) => {
  if (group.sheetSizeSelection !== 'automatic') return;
  const { parts } = useCutlistState.getState();
  const groupParts = parts.filter((p) => p.groupId === group.id);
  const updatedCore = getSmallestFittingMaterial(
    groupParts,
    group.automaticSheetSizeMaterials
  );

  if (group.core1 != updatedCore) {
    const updatedGroup = { ...group, core1: updatedCore };
    setGroupAndParts(updatedGroup);
  }
};

export const findBeamMaterial = async (
  articleCode: string
): Promise<Material | null> => {
  try {
    const beamMaterials = await api.beamMaterials({});
    return (
      beamMaterials?.find(
        (material: Material) => material.articleCode === articleCode
      ) || null
    );
  } catch (error) {
    console.error('Failed to fetch beam materials:', error);
    return null;
  }
};

export const articleCodeToMaterialGroupMapper = async (
  articleCode: string,
  currentFeatures: Features
) => {
  const { supportedSheetEdgeTrims, defaultSheetEdgeTrimTypeForPartGroup } =
    currentFeatures;

  let groupParam: Partial<MaterialGroup> = {
    core1: articleCode,
    materialSandwichType: 'single-core',
    type: 'panels-and-strips',
    sheetEdgeTrimConfig: getTrimConfigByType(
      supportedSheetEdgeTrims,
      defaultSheetEdgeTrimTypeForPartGroup
    ),
  };
  const hplMaterials = getHPLMaterials();
  const isHpl = !!hplMaterials.find((x) => x.articleCode === articleCode);
  const edgebandMaterials = getEdgebandingMaterials();
  const isEdgeband = !!edgebandMaterials.find(
    (x) => x.articleCode === articleCode
  );
  if (isHpl) {
    groupParam = {
      topHpl: articleCode,
      bottomHpl: articleCode,
      materialSandwichType: 'single-core-double-layer',
    };
  }
  if (isEdgeband) {
    groupParam = {
      edgeband: articleCode,
      materialSandwichType: 'single-core-double-layer',
    };
  }

  const materialGroup = generateMaterialGroup(groupParam);

  const material = getMaterial(articleCode);
  if (groupParam.type === 'panels-and-strips') {
    applyCustomTrimThickness(materialGroup, material);
  }

  return materialGroup;
};

export const useMaterialGroupState = create<
  MaterialGroupState & MaterialGroupStateActions
>()(
  devtools(
    (set, get) => ({
      groups: [],
      init: (state) => {
        const groups = [...state.groups];

        set(() => {
          return { ...state, groups };
        });
      },
      groupBy: () => {
        if (get().groups?.length) return;

        const { parts, setPart } = useCutlistState.getState();
        const groups = groupPartsByMaterial(parts);
        const materialGroups: MaterialGroup[] = [];
        const newParts: PartItem[] = [];

        if (!groups.size) {
          materialGroups.push(generateMaterialGroup());
        }

        groups.forEach((groupParts) => {
          if (!groupParts.length) return;

          const part = groupParts[0];
          const materialGroup = generateMaterialGroup({
            core1: part.core1,
            core2: part.core2,
            topHpl: part.topHpl,
            bottomHpl: part.bottomHpl,
          });

          materialGroups.push(materialGroup);
          groupParts.forEach((part) => {
            newParts.push({ ...part, groupId: materialGroup.id });
          });
        });

        set(() => ({
          groups: materialGroups,
          activeGroup: materialGroups[0].id,
        }));
        newParts.forEach((part) => setPart(part));
      },
      addGroup: (group = {}) => {
        const materialGroup = generateMaterialGroup(group);
        set((state) => {
          return {
            groups: [...state.groups, materialGroup],
            activeGroup: materialGroup.id,
          };
        });

        return materialGroup.id;
      },
      addGroupsByArticleCodes: async (
        articleCodes,
        currentFeatures,
        activeGroupArticleCode
      ) => {
        const newGroups = await Promise.all(
          articleCodes.map((articleCode) =>
            articleCodeToMaterialGroupMapper(articleCode, currentFeatures)
          )
        );

        set((state) => {
          const groups = [...state.groups, ...newGroups];

          const activeGroupId = groups.find(
            (g) => g.core1 === activeGroupArticleCode
          )?.id;

          return {
            groups,
            activeGroup: activeGroupId || state.activeGroup || groups[0]?.id,
          };
        });
      },
      addBeamGroupByArticleCode: (beamMaterial: Material) => {
        const groupStore = useMaterialGroupState.getState();
        if (beamMaterial) {
          const newGroupId = groupStore.addGroup({
            beamMaterialId: beamMaterial.id,
            type: 'beams',
            name: beamMaterial.variationGroup?.name || beamMaterial.name,
          });

          const groups = useMaterialGroupState.getState().groups;

          const materialGroup = groups.find((g) => g.id === newGroupId);

          const { addPart, setPart } = useCutlistState.getState();

          const part = addPart(materialGroup!, 'beam');

          const updatedPart: PartItem = {
            ...part,
            partType: 'beam',
            lengthMM: beamMaterial.lengthMM,
            widthMM: beamMaterial.widthMM,
            thickness: beamMaterial.thicknessUM,
            beamMaterialId: beamMaterial.id,
            quantity: 1,
          };

          setPart(updatedPart);
        }
      },
      setGroup: (newGroup) => {
        set((state) => {
          const index = state.groups.findIndex((g) => g.id === newGroup.id);

          if (index === -1) {
            Sentry.captureException(
              'setGroup on non existing group. This should never happen.',
              {
                extra: {
                  groupId: newGroup.id,
                  groups: state.groups,
                },
              }
            );
          }

          const oldGroup = state.groups[index];
          const groups = state.groups.slice(0);

          if (oldGroup.core1 !== newGroup?.core1) {
            newGroup.edgeProfile = 'none';
          }

          groups[index] = newGroup;

          return { groups };
        });
      },
      removeGroup: (id) => {
        const { removePartsByGroup } = useCutlistState.getState();
        removePartsByGroup(id);
        set((state) => {
          const isSelected = state.activeGroup === id;
          const idx = state.groups.findIndex((g) => g.id == id);
          const groups = state.groups.filter((g) => g.id !== id);
          const next = Math.max(0, idx - 1);
          return {
            activeGroup: isSelected ? groups[next]?.id : state.activeGroup,
            groups,
          };
        });
      },
      removeEmptyGroups: () => {
        set((state) => {
          const groups = state.groups.filter(
            (g) => g.core1 || g.beamMaterialId
          );
          return { groups };
        });
      },
      setActive: (id) => set(() => ({ activeGroup: id })),
      clear: () => set(() => ({ groups: [], activeGroup: undefined })),

      addBackendGroup: (group: MaterialGroup) => {
        const groups = [...get().groups, group];

        set(() => {
          return { activeGroup: group.id, groups };
        });
      },
      setGroupIndex: (groupId, index) => {
        set((state) => {
          const position = state.groups.findIndex((g) => g.id === groupId);
          const group = state.groups[position];
          const groups = state.groups.slice(0);

          groups[position] = { ...group, index };

          return { groups };
        });
      },
    }),
    { name: 'materialsGroup' }
  )
);

export const useMaterialGroups = () =>
  useMaterialGroupState((state) => state.groups);

const groupBySelector = (state: MaterialGroupStateActions) => state.groupBy;
export const useGroupBy = () => useMaterialGroupState(groupBySelector);

export const activeGroupSelector = (state: MaterialGroupState) =>
  state.groups.find((g) => g.id === state.activeGroup);
export const useActiveGroup = () => useMaterialGroupState(activeGroupSelector);

export const useActiveGroupType = () =>
  useMaterialGroupState((state) => activeGroupSelector(state)?.type);

export const useActiveGroupParts = () => {
  const activeGroupId = useMaterialGroupState((state) => state.activeGroup);
  return useCutlistState(
    useShallow((state) =>
      state.parts.filter((p) => p.groupId === activeGroupId)
    )
  );
};

const groupSelectorById = (state: MaterialGroupState, id: string) =>
  state.groups.find((g) => g.id === id);
export const useGroupById = (id: string) =>
  useMaterialGroupState((state) => groupSelectorById(state, id));

export const edgebandSelector = (state: MaterialGroupState) => {
  const activeGroup = activeGroupSelector(state);
  return Boolean(activeGroup?.edgeband);
};
export const useEdgebandSelected = () =>
  useMaterialGroupState(edgebandSelector);

export const sheetEdgeTrimConfigSelector = (state: MaterialGroupState) => {
  const activeGroup = activeGroupSelector(state);
  return activeGroup?.sheetEdgeTrimConfig;
};
export const useSheetEdgeTrimConfig = () =>
  useMaterialGroupState(sheetEdgeTrimConfigSelector);

export const useActiveGroupMaterials = () => {
  const group = useActiveGroup();
  if (!group) return {};

  const [core1, core2, topHpl, bottomHpl] = [
    group.core1,
    group.core2,
    group.topHpl,
    group.bottomHpl,
  ].map(getMaterial);

  return { core1, core2, topHpl, bottomHpl };
};

export const useMatchingMaterials = () => {
  const edgebandingMaterials = getEdgebandingMaterials();
  const sheetMaterials = useActiveGroupMaterials();
  return findMatchingMaterialsWithFallback(
    sheetMaterials,
    edgebandingMaterials
  );
};

export const useActiveGroupGrainDirection = () => {
  const materials = useActiveGroupMaterials();

  // Subscribe to hasMaterials so this component re-renders when the materials
  // cache is populated (getMaterial reads from a non-reactive module variable).
  useCutlistState(selectHasMaterials);

  if (materials.topHpl || materials.bottomHpl) {
    const topHplGrainDirection = materials.topHpl?.hasGrainDirection;
    const topHplTextureDirection = materials.topHpl?.hasTextureDirection;
    const bottomHplGrainDirection = materials.bottomHpl?.hasGrainDirection;
    const bottomHplTextureDirection = materials.bottomHpl?.hasTextureDirection;
    return [
      topHplGrainDirection,
      topHplTextureDirection,
      bottomHplGrainDirection,
      bottomHplTextureDirection,
    ].some((it) => it === true);
  }
  return (
    materials.core1?.hasGrainDirection || materials.core1?.hasTextureDirection
  );
};

export const useEdgebandingMaterialForGroup = () => {
  const group = useActiveGroup();
  const parts = useCutlistParts();

  return Array.from(
    new Set(
      parts
        // you only want to check against the active group
        .filter((p) => p.groupId === group?.id)
        .flatMap((p) => [
          p.edgebanding?.length1,
          p.edgebanding?.length2,
          p.edgebanding?.width1,
          p.edgebanding?.width2,
        ])
    )
  ).filter(Boolean);
};

export function makeTitle(group: MaterialGroup) {
  const material = getMaterial(group.core1);
  const coreName =
    group.sheetSizeSelection === 'automatic'
      ? material?.variationGroup?.name || material?.name
      : material?.name;
  return [coreName || group.core1, group.topHpl, group.bottomHpl]
    .filter(Boolean)
    .join(' / ');
}

export const useSaveSensitiveData = () => {
  const group = useActiveGroup();
  const debouncedGroup = useDebounce<MaterialGroup | undefined>(group, 500);
  const { title, deliverLeftoverMaterials } = useCutlistState();
  const debouncedTitle = useDebounce<string>(title, 500);

  const { continuousGrain, additionalProcessing } = debouncedGroup || {};

  const saveSensitiveData = React.useMemo(() => {
    const hasNotes = Boolean(continuousGrain || additionalProcessing);

    return {
      hasNotes,
      debouncedTitle,
      deliverLeftoverMaterials,
    };
  }, [
    continuousGrain,
    additionalProcessing,
    debouncedTitle,
    deliverLeftoverMaterials,
  ]);

  return saveSensitiveData;
};

export const useActiveGroupPricingSensitiveData = () => {
  const activeGroup = useActiveGroup();
  const { discountAmount, discountPercentage, markupAmount, markupPercentage } =
    useCutlistState();
  let {
    paintColor = null,
    paintThicknessUM = 0,
    edgeProfile,
    createLabels,
  } = activeGroup || {};
  const isLoggedIn = useIsLoggedIn();

  if (!paintThicknessUM) paintThicknessUM = 0;

  const priceSensitiveData = React.useMemo(() => {
    return {
      paintColor,
      paintThicknessUM,
      edgeProfile,
      createLabels,
      isLoggedIn,
      discountAmount,
      discountPercentage,
      markupAmount,
      markupPercentage,
    };
  }, [
    paintColor,
    paintThicknessUM,
    edgeProfile,
    createLabels,
    isLoggedIn,
    discountAmount,
    discountPercentage,
    markupAmount,
    markupPercentage,
  ]);

  return priceSensitiveData;
};

export const useHasGroupChanged = ({ groupId }: { groupId: string }) => {
  const groups = useMaterialGroups();
  const parts = useCutlistState((state) => state.parts);
  const group = groups.find((g) => g.id === groupId);

  if (!group) return false;

  return !isGroupSaved(
    group,
    parts.filter((p) => p.groupId === groupId)
  );
};

export const useUnsavedGroups = () => {
  const groups = useMaterialGroups();
  const parts = useCutlistState((state) => state.parts);

  const unsavedGroups = groups.filter(
    (g) =>
      !isGroupSaved(
        g,
        parts.filter((p) => p.groupId === g.id)
      )
  );

  return unsavedGroups;
};

export const isGroupSaved = (group: MaterialGroup, groupParts: PartItem[]) => {
  const savedStateHash = group.hash;

  const currentStateHash = hashGroup(group, groupParts);

  return currentStateHash === savedStateHash;
};

/**
 * @returns nestingResult
 * nestingResult is the actual nesting result or null if the group is not nested
 */
export const useBackendGroup = ({
  groupId,
}: {
  groupId: string;
}): BackendCutlistGroup | null => {
  const backendGroups = useBackendGroups();

  const backendGroup = backendGroups?.find(
    (g) => g.materialGroup.groupId === groupId
  );

  return backendGroup || null;
};

/**
 * @returns backendGroups
 * backendGroups contains all the material groups together with nesting data, mapped to the BE structure
 */
export const useBackendGroups = (): BackendCutlistGroup[] | null => {
  const sheetGroups = useNestingStore((state) => state.sheetGroups);
  const { groups: materialGroups } = useMaterialGroupState();

  const backendGroups = isStarterTier()
    ? createBackendGroupingForNesting(materialGroups)
    : createBackendGrouping(sheetGroups, materialGroups);

  if (!backendGroups?.length) {
    return [];
  }

  return backendGroups;
};

/**
 * @returns [saveMaterialGroup, canSaveMaterialGroup, isSaving]
 * saveMaterialGroup is the function to save the material group
 * canSaveMaterialGroup is true if the material group can be saved
 * isSaving is true if the material group is still being saved
 */
export const useSaveGroupChanges = ({
  group,
}: {
  group: MaterialGroup;
}): [() => Promise<void>, boolean] => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { mutateAsync: updateMaterialGroup, isPending: isSaving } =
    useUpdateMaterialGroup(id as string);
  const nestingResult = useBackendGroup({ groupId: group.id });
  const hydrateMaterialGroup = useHydrateMaterialGroup();
  const hasGroupChanged = useHasGroupChanged({ groupId: group.id });
  const nestingStatus = useNestingStatusStore((state) => state.status);

  const isNestingRunning = nestingStatus[group.id] === 'running';
  const canSave =
    !!nestingResult &&
    Boolean(group.ulid) &&
    hasGroupChanged &&
    !isSaving &&
    !isNestingRunning;

  const handleSaveMaterialGroup = useCallback(async () => {
    if (!nestingResult) return;
    if (!group.ulid) return;

    const backendGroups = await updateMaterialGroup({
      materialGroupId: group.ulid,
      materialGroup: nestingResult,
    });

    hydrateMaterialGroup({
      backendGroups,
      groupId: group.id,
    });

    toast.success(t('agent.toasts.materialSettingsSaved'));
  }, [
    nestingResult,
    group.ulid,
    group.id,
    updateMaterialGroup,
    hydrateMaterialGroup,
    t,
  ]);

  return [handleSaveMaterialGroup, canSave];
};

/**
 * @returns [createMaterialGroup, canCreateMaterialGroup, isCreating]
 * createMaterialGroup is the function to create a new material group
 * canCreateMaterialGroup is true if the material group can be created
 * isCreating is true if the material group is still being created
 */
export const useCreateGroup = ({
  group,
}: {
  group: MaterialGroup;
}): [() => Promise<void>, boolean] => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { mutateAsync: createMaterialGroup, isPending: isCreating } =
    useCreateMaterialGroup(id as string);
  const nestingResult = useBackendGroup({ groupId: group.id });
  const hydrateMaterialGroup = useHydrateMaterialGroup();
  const nestingStatus = useNestingStatusStore((state) => state.status);

  const hasRequiredMaterial =
    group.type === 'beams'
      ? Boolean(group.beamMaterialId)
      : Boolean(group.core1);
  const isNestingRunning = nestingStatus[group.id] === 'running';
  const canCreate =
    !!nestingResult && hasRequiredMaterial && !isCreating && !isNestingRunning;

  const handleCreateMaterialGroup = useCallback(async () => {
    if (!nestingResult) return;
    const backendGroups = await createMaterialGroup(nestingResult);

    hydrateMaterialGroup({
      backendGroups,
      groupId: group.id,
    });

    toast.success(t('agent.toasts.materialAddedToOrder'));
  }, [nestingResult, createMaterialGroup, group.id, hydrateMaterialGroup, t]);

  return [handleCreateMaterialGroup, canCreate];
};

/**
 * @returns [saveMaterialGroup, canSaveMaterialGroup, isSaving]
 * saveMaterialGroup is the function to save the material group
 * canSaveMaterialGroup is true if the material group can be saved
 * isSaving is true if the material group is still being saved
 */
export const useSaveAllGroupChanges = (): [() => Promise<number>] => {
  const { id } = useParams();
  const hydrateMaterialGroup = useHydrateMaterialGroup();
  const unsavedGroups = useUnsavedGroups();
  const backendGroups = useBackendGroups();

  const { mutateAsync: createMaterialGroup } = useCreateMaterialGroup(
    id as string
  );
  const { mutateAsync: updateMaterialGroup } = useUpdateMaterialGroup(
    id as string
  );

  const handleSaveAllMaterialGroups = useCallback(async (): Promise<number> => {
    if (!backendGroups) return Promise.resolve(0);
    if (!backendGroups.length) return Promise.resolve(0);
    if (!unsavedGroups.length) return Promise.resolve(0);

    const unsavedIds = unsavedGroups.map((g) => g.id);
    const unsavedBackendGroups = backendGroups.filter(
      (g) =>
        unsavedIds.includes(g.materialGroup.groupId) &&
        (g.materialGroup.core1MaterialId || g.materialGroup.beamMaterialId)
    );

    for (const group of unsavedBackendGroups) {
      if (group.materialGroup.ulid) {
        const backendGroups = await updateMaterialGroup({
          materialGroupId: group.materialGroup.ulid,
          materialGroup: group,
        });

        hydrateMaterialGroup({
          backendGroups,
          groupId: group.materialGroup.groupId,
        });
      }

      if (!group.materialGroup.ulid) {
        const backendGroups = await createMaterialGroup(group);

        hydrateMaterialGroup({
          backendGroups,
          groupId: group.materialGroup.groupId,
        });
      }
    }

    return Promise.resolve(unsavedBackendGroups.length);
  }, [
    backendGroups,
    unsavedGroups,
    updateMaterialGroup,
    createMaterialGroup,
    hydrateMaterialGroup,
  ]);

  return [handleSaveAllMaterialGroups];
};

/**
 * @returns hydrateMaterialGroups
 * hydrateMaterialGroups is a function which:
 * - removes the old group with groupId
 * - adds the new group with backend data
 * - sets the parts for the new group
 * - resets all the groups indexes
 */
const useHydrateMaterialGroup = () => {
  const { setPart } = useCutlistState();

  const { addBackendGroup, removeGroup, setGroupIndex } =
    useMaterialGroupState();

  const handler = useCallback(
    async ({
      backendGroups,
      groupId,
    }: {
      backendGroups: CutlistMaterialGroup[];
      groupId: string;
    }) => {
      const backendGroup = backendGroups.find(
        (g) => g.groupId === groupId
      ) as CutlistMaterialGroup;

      if (!backendGroup) {
        Sentry.captureException(
          'New group (before mapping to FE structure) not found. This should never happen.',
          // @ts-expect-error Own error type
          { extra: backendGroups }
        );
        return;
      }

      const newGroup = materialGroupMapper(backendGroup);
      removeGroup(groupId);
      addBackendGroup(newGroup);

      backendGroup.parts.forEach((part) => {
        const partItem = partMapper(part);
        setPart(partItem, newGroup);
      });

      backendGroups.forEach((g) => {
        setGroupIndex(g.groupId, g.index);
      });
    },
    [addBackendGroup, removeGroup, setGroupIndex, setPart]
  );

  return handler;
};

export const getGroupGrainDirection = (
  group: MaterialGroup | undefined,
  parts: PartItem[]
) => {
  if (!parts.length) {
    return getMaterial(group?.core1)?.hasGrainDirection ? 'along' : 'none';
  }

  return parts.some((p) => p.grainDirection === 'along') ? 'along' : 'none';
};

export const useCurrentGroupGrainDirection = () =>
  getGroupGrainDirection(useActiveGroup(), useActiveGroupParts());

export const hashGroup = (group: MaterialGroup, groupParts: PartItem[]) => {
  // strip fields that might change
  const groupWithoutHash = { ...group, hash: undefined, index: undefined };

  // For automatic groups, core1 is set by the nesting orchestrator after
  // picking the best sheet size — exclude it so the group doesn't appear
  // unsaved right after nesting completes.
  if (group.sheetSizeSelection === 'automatic') {
    groupWithoutHash.core1 = undefined as unknown as string;
  }

  // strip part fields that might change or are irrellevant for the hash
  const groupPartsWithoutHash = groupParts.map((p) => ({
    ...p,
    createdAt: undefined,
  }));

  const currentStateHash = hashObject({
    ...groupWithoutHash,
    parts: groupPartsWithoutHash,
  });

  return currentStateHash;
};

export const getGroupNestingFields = (group: MaterialGroup) => {
  const fields = [
    'core2',
    'topHpl',
    'bottomHpl',
    'materialSandwichType',
    'sheetEdgeTrimConfig',
    'sheetSizeSelection',
    'automaticSheetSizeMaterials',
    'type',
  ] as (keyof MaterialGroup)[];

  // Include core1 only for manual groups — for automatic groups,
  // core1 is the orchestrator's output and would cause a re-trigger loop.
  if (group.sheetSizeSelection !== 'automatic') {
    fields.unshift('core1');
  }

  return pick(group, fields);
};

export const getPartNestingFields = (p: PartItem) => {
  return pick(p, [
    'groupId',
    'label',
    'quantity',
    'grainDirection',
    'widthMM',
    'lengthMM',
    'thickness',
    'core1',
    'core2',
    'topHpl',
    'bottomHpl',

    // For customers, nesting triggers saving
    // so we have to re-nest even if these fields don't impact nesting just to save them
    'grooves',
    'edgebanding',
    'roundedEdgeband',
    'edgeProfile',
    'createLabel',
    'partType',
  ]);
};

export const getAgentPartNestingFields = (p: PartItem) => {
  return omit(getPartNestingFields(p) as Record<string, unknown>, [
    'label',
    'roundedEdgeband',
    'edgeProfile',
    'createLabel',
  ]);
};

export const hashGroupForNesting = (
  group: MaterialGroup,
  groupParts: PartItem[],
  getNestingSensitiveFields: (p: PartItem) => object = getPartNestingFields
) => {
  // strip fields that might change
  const nestingSensitiveFields = getGroupNestingFields(group);

  // strip part fields that might change or are irrellevant for the hash
  const groupPartsNestingSensitiveFields = groupParts.map((p) => ({
    ...getNestingSensitiveFields(p),
  }));

  const currentStateHash = hashObject({
    ...nestingSensitiveFields,
    parts: groupPartsNestingSensitiveFields,
  });

  return currentStateHash;
};
